import type { MobilePayload, UserDTO } from '@klangio/ai2notes-ts-client';
import { AuthService, UsersService } from '@klangio/ai2notes-ts-client';
import sum from 'lodash/sum';
import type { PropsWithChildren } from 'react';
import { useContext, useEffect, useMemo } from 'react';
import * as React from 'react';
import { backendService, trackingService } from '../../services';
import {
  addStoredTranscriptionIdsToUser,
  AUTH_URL,
  filterSubscriptions,
  isJwtTokenValid,
} from '../../utils';
import type { IUserContextValue } from './UserContext';
import UserContext from './UserContext';
import { AppConstantsContext } from '../AppConstants';

export const MUSE_ID_KEY = 'muse_id';

const UserProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = React.useState<UserDTO | null>(null);
  const [isLoadingUserCompleted, setIsLoadingUserCompleted] = React.useState<boolean>(false);
  const [isClaimingTranscriptions, setIsClaimingTranscriptions] = React.useState<boolean>(false);
  const { jobType } = useContext(AppConstantsContext);

  const numberOfTickets = useMemo(() => {
    if (user) {
      const subscriptionTickets = sum(user.subscriptions.map((s) => s.tickets));
      return subscriptionTickets + user.tickets;
    }
    return 0;
  }, [user]);

  const hasTickets = useMemo(() => {
    return numberOfTickets > 0;
  }, [numberOfTickets]);

  const getUserWithFilteredSubscription = (userData: UserDTO): UserDTO => {
    const subs = filterSubscriptions((window as Window).DOMAIN, userData?.subscriptions);

    return {
      ...userData,
      subscriptions: subs,
    };
  };

  const logout = () => {
    localStorage.removeItem('currentUser');
    setTimeout(() => {
      window.location.href = `${AUTH_URL}/logout`;
    }, 500);
    return Promise.resolve();
  };

  const getUser = async () => {
    try {
      /**
       * Login method for musehub app
       */
      const existingId = localStorage.getItem(MUSE_ID_KEY);
      const searchContent = window.location.search;
      if (
        typeof existingId === 'string'
        || (searchContent && searchContent.includes(MUSE_ID_KEY))
      ) {
        console.log('Found muse_id in url');
        const museId = new URLSearchParams(window.location.search).get(MUSE_ID_KEY) || existingId;
        console.log('MuseId: ', museId);
        localStorage.setItem(MUSE_ID_KEY, museId);

        const payload: MobilePayload = {
          uniqueId: `muse:${museId}`,
          initialRegistrationDomain: jobType,
        };
        console.log('Payload: ', payload);
        const userRequest = (await UsersService.usersSignInMobile(payload)) as unknown as UserDTO;
        console.log('UserRequest: ', userRequest);

        if (userRequest?.token) {
          backendService.setOpenApiToken(userRequest?.token);
          const response = await UsersService.usersMePost();
          console.log('refreshed user: ', response);
          const newUser = getUserWithFilteredSubscription(response);
          newUser.token = userRequest?.token;
          setUser(newUser);
          trackingService.setUserId(newUser.id);
          localStorage.setItem('currentUser', JSON.stringify(newUser));
          return newUser;
        }
      }
      /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

      if (window.location.hostname === 'localhost') {
        const newUser = JSON.parse(localStorage.getItem('currentUser'));
        setUser(newUser);
        backendService.setOpenApiToken(newUser?.token);
        return newUser;
      }

      const userRequest = await AuthService.authExtractSessionTokenFromCookie();
      if (!userRequest?.token) {
        console.error('No token received from backend.');
      } else {
        backendService.setOpenApiToken(userRequest?.token);
        const response = await UsersService.usersMePost();
        const newUser = getUserWithFilteredSubscription(response);
        newUser.token = userRequest?.token;
        setUser(newUser);
        trackingService.setUserId(newUser.id);
        localStorage.setItem('currentUser', JSON.stringify(newUser));
        if (!isJwtTokenValid()) {
          setUser(null);
          logout();
        }
        return newUser;
      }
    } catch (e) {
      console.error(e);
      if ([401, 403].indexOf(e.status) !== -1) {
        if (localStorage.getItem('currentUser')) {
          // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
          logout();
        }
      }
      return null;
    }
    return null;
  };

  useEffect(() => {
    backendService.initOpenApi();
    if (typeof window !== 'undefined') {
      if (!isLoadingUserCompleted) {
        getUser().finally(() => {
          setIsLoadingUserCompleted(true);
        });
      }
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (user && !isClaimingTranscriptions && isLoadingUserCompleted) {
      setIsClaimingTranscriptions(true);
      addStoredTranscriptionIdsToUser();
    }
  }, [user, isClaimingTranscriptions, isLoadingUserCompleted]);

  const refresh = async () => {
    const refreshedUser = await getUser();
    return Promise.resolve(refreshedUser);
  };

  const hasSubscriptionTickets = () => {
    if (user) {
      return numberOfTickets > 0;
    }
    return false;
  };

  const hasSubscription = () => {
    if (user) {
      return user.subscriptions?.length > 0;
    }
    return false;
  };

  const handleDeleteUser = async () => {
    try {
      const vals = await UsersService.usersDelete();
      await logout();
      return vals;
    } catch (e) {
      console.log('Logout error');
      console.error(e);
      throw e;
    }
  };

  const value = React.useMemo<IUserContextValue>(
    () => ({
      user,
      numberOfTickets,
      hasTicketsForUse: hasTickets,
      hasSubscriptionTickets: hasSubscriptionTickets(),
      hasSubscription: hasSubscription(),
      logout,
      refresh,
      isLoadingUserCompleted,
      delete: handleDeleteUser,
    }),
    [user, isLoadingUserCompleted, numberOfTickets],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserProvider;
